import React from 'react';
import { Case, Client } from '../../types';
import { CaseCard } from './CaseCard';
import { groupCasesByStudent, sortCasesByStudentName, GroupedCases } from './caseViewUtils';

/**
 * Props for the CaseList component
 */
export interface CaseListProps {
  cases: Case[];
  clients: Map<string, Client>;
  onCaseClick: (caseId: string) => void;
}

/**
 * Pure function to get client name from clients map
 * @param clientId - The client ID
 * @param clients - Map of client ID to Client
 * @returns The formatted client name or 'Unknown' if not found
 */
export function getClientName(clientId: string, clients: Map<string, Client>): string {
  const client = clients.get(clientId);
  if (!client) {
    return 'Unknown';
  }
  return `${client.firstName} ${client.lastName}`;
}

/**
 * Pure function to process cases into sorted and grouped structure
 * @param cases - Array of cases
 * @returns Array of grouped cases sorted by student name
 */
export function processCasesForDisplay(cases: Case[]): GroupedCases[] {
  const sortedCases = sortCasesByStudentName(cases);
  return groupCasesByStudent(sortedCases);
}

/**
 * CaseList component - renders grouped cases with student name headers.
 * 
 * Displays:
 * - Cases grouped by student name with headers
 * - CaseCard for each individual case
 * - Empty state when no cases exist
 * 
 * Requirements: 2.2, 6.3
 */
export const CaseList: React.FC<CaseListProps> = ({ cases, clients, onCaseClick }) => {
  // Handle empty state
  if (cases.length === 0) {
    return (
      <div
        data-testid="case-list-empty"
        style={{
          textAlign: 'center',
          padding: '48px 24px',
          color: '#666',
        }}
      >
        <p style={{ fontSize: '16px', margin: 0 }}>No cases found</p>
      </div>
    );
  }

  const groupedCases = processCasesForDisplay(cases);

  return (
    <div data-testid="case-list">
      {groupedCases.map((group) => (
        <div
          key={group.studentKey}
          data-testid={`case-group-${group.studentKey}`}
          style={{ marginBottom: '24px' }}
        >
          {/* Student name header */}
          <h2
            data-testid={`case-group-header-${group.studentKey}`}
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#333',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '2px solid #e0e0e0',
            }}
          >
            {group.studentName}
          </h2>

          {/* Cases for this student */}
          <div>
            {group.cases.map((caseItem) => (
              <CaseCard
                key={caseItem.caseId}
                case={caseItem}
                clientName={getClientName(caseItem.clientId, clients)}
                onClick={() => onCaseClick(caseItem.caseId)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
